import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Button, Badge, Spinner, Breadcrumb, Form } from 'react-bootstrap';
import { LuPlus, LuMinus, LuArrowLeft, LuBox, LuChevronLeft, LuChevronRight } from "react-icons/lu";
import PortalAxios, { baseURL } from '../../services/PortalAxios';
import { addToCart, updateCartItem } from '../../redux/reducers/portalCartSlice';
import { formatCurrency } from '../../utils/currency';

const PortalProductDetail = () => {
    const { id, businessId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [activeImage, setActiveImage] = useState(0);

    // Zoom state
    const [isZooming, setIsZooming] = useState(false);
    const [zoomPosition, setZoomPosition] = useState({ bgX: 0, bgY: 0, lensX: 0, lensY: 0 });
    const imgRef = useRef(null);
    const isMobile = window.innerWidth <= 768;

    // Carousel state
    const [thumbnailOffset, setThumbnailOffset] = useState(0);

    const { cart } = useSelector((state) => state.portalCart);
    const currency = useSelector((state) => state.portalAuth?.businessData?.jssettings?.currency || 'CAD');

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const res = await PortalAxios.PortalRequest({
                    method: 'get',
                    url: `${baseURL}/portal/products/${id}`
                });
                setProduct(res.data.data);
                
                const inCart = cart?.items?.find(i => i.productId === id);
                if (inCart) setQuantity(inCart.quantity);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();
    }, [id, cart]);

    const handleAddToCart = () => {
        const inCart = cart?.items?.find(i => i.productId === id);
        if (inCart) {
            dispatch(updateCartItem({ productId: id, quantity }));
        } else {
            dispatch(addToCart({ productId: id, quantity }));
        }
    };

    const getTieredPrice = (qty) => {
        if (!product?.pricingTiers || product.pricingTiers.length === 0) return product?.price || 0;
        const sortedTiers = [...product.pricingTiers].sort((a, b) => b.minQuantity - a.minQuantity);
        const tier = sortedTiers.find(t => qty >= t.minQuantity);
        return tier ? tier.price : product.price;
    };

    const currentPrice = getTieredPrice(quantity);

    // Carousel Math
    const visibleCount = 4;
    const canPrev = thumbnailOffset > 0;
    const canNext = product?.images ? thumbnailOffset < (product.images.length - visibleCount) : false;

    const handlePrev = () => setThumbnailOffset(prev => Math.max(0, prev - 1));
    const handleNext = () => setThumbnailOffset(prev => Math.min(product?.images?.length - visibleCount, prev + 1));

    // Arrow Style
    const arrowStyle = {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: 'white',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        zIndex: 10,
        padding: 0
    };

    // Zoom Handlers
    const handleMouseMove = (e) => {
        if (!imgRef.current) return;
        const rect = imgRef.current.getBoundingClientRect();
        const xPercent = (e.clientX - rect.left) / rect.width;
        const yPercent = (e.clientY - rect.top) / rect.height;

        const bgX = xPercent * 100;
        const bgY = yPercent * 100;

        setZoomPosition({ bgX, bgY, lensX: 0, lensY: 0 });
    };

    const handleMouseEnter = () => {
        if (!isMobile) setIsZooming(true);
    };

    const handleMouseLeave = () => {
        setIsZooming(false);
    };

    const handleMainImgPrev = (e) => {
        e.stopPropagation();
        if (product?.images?.length > 1) {
            const nextIdx = activeImage === 0 ? product.images.length - 1 : activeImage - 1;
            setActiveImage(nextIdx);
        }
    };

    const handleMainImgNext = (e) => {
        e.stopPropagation();
        if (product?.images?.length > 1) {
            const nextIdx = activeImage === product.images.length - 1 ? 0 : activeImage + 1;
            setActiveImage(nextIdx);
        }
    };

    if (loading) return <div className="text-center py-5"><Spinner animation="border" style={{ color: "#28B0C4" }} /></div>;
    if (!product) return <div className="text-center py-5">Product not found.</div>;

    return (
        <div>
            <Breadcrumb className="mb-4">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/customer-side/${businessId}/dashboard` }}>Dashboard</Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/customer-side/${businessId}/products` }}>Products</Breadcrumb.Item>
                <Breadcrumb.Item active>{product.name}</Breadcrumb.Item>
            </Breadcrumb>

            <Row className="g-5">
                <Col lg={6}>
                    <div className="product-gallery position-relative" style={{ overflow: 'visible' }}>
                        <div className="position-relative mb-3">
                            <div 
                                className="main-image bg-light rounded-4 overflow-hidden position-relative d-flex align-items-center justify-content-center" 
                                style={{ height: '500px', cursor: isZooming && !isMobile ? 'zoom-in' : 'default' }}
                                onMouseMove={handleMouseMove}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                                ref={imgRef}
                            >
                                {product.images?.[activeImage] ? (
                                    <img 
                                        src={product.images[activeImage]} 
                                        alt={product.name} 
                                        style={{ 
                                            width: '100%', 
                                            height: '100%', 
                                            objectFit: 'contain',
                                            transform: isZooming && !isMobile ? 'scale(2.5)' : 'scale(1)',
                                            transformOrigin: `${zoomPosition.bgX}% ${zoomPosition.bgY}%`,
                                            transition: isZooming ? 'none' : 'transform 0.3s ease',
                                            pointerEvents: 'none'
                                        }} 
                                    />
                                ) : (
                                    <LuBox size={100} className="text-muted opacity-25" />
                                )}
                            </div>

                            {/* Main Image Arrows */}
                            {product.images?.length > 1 && (
                                <>
                                    <button 
                                        onClick={handleMainImgPrev} 
                                        style={{ ...arrowStyle, left: '-16px' }} 
                                        className="carousel-arrow prev"
                                    >
                                        <LuChevronLeft size={16} style={{ color: '#28B0C4' }} />
                                    </button>
                                    <button 
                                        onClick={handleMainImgNext} 
                                        style={{ ...arrowStyle, right: '-16px' }} 
                                        className="carousel-arrow next"
                                    >
                                        <LuChevronRight size={16} style={{ color: '#28B0C4' }} />
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Carousel Feature */}
                        {product.images?.length > 1 && (
                            <div className="thumbnail-carousel position-relative mt-3">
                                {/* Prev arrow */}
                                {canPrev && (
                                    <button onClick={handlePrev} style={{ ...arrowStyle, left: 0 }} className="carousel-arrow prev">
                                        <LuChevronLeft size={16} style={{ color: '#28B0C4' }} />
                                    </button>
                                )}

                                {/* Thumbnail track */}
                                <div style={{ overflow: 'hidden', margin: '0 40px' }}>
                                    <div style={{
                                        display: 'flex',
                                        gap: '8px',
                                        transform: `translateX(calc(-${thumbnailOffset} * (80px + 8px)))`,
                                        transition: 'transform 0.3s ease'
                                    }}>
                                        {product.images.map((img, idx) => (
                                            <div
                                                key={idx}
                                                onClick={() => { setActiveImage(idx); setThumbnailOffset(0) }}
                                                style={{
                                                    width: 80,
                                                    height: 80,
                                                    flexShrink: 0,
                                                    borderRadius: 12,
                                                    overflow: 'hidden',
                                                    border: activeImage === idx ? '2px solid #28B0C4' : '2px solid transparent',
                                                    transform: activeImage === idx ? 'scale(1.05)' : 'scale(1)',
                                                    opacity: activeImage === idx ? 1 : 0.7,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Next arrow */}
                                {canNext && (
                                    <button onClick={handleNext} style={{ ...arrowStyle, right: 0 }} className="carousel-arrow next">
                                        <LuChevronRight size={16} style={{ color: '#28B0C4' }} />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </Col>

                <Col lg={6}>
                    <div className="ps-lg-4">
                        <Badge bg="primary-subtle" className="text-primary mb-2 rounded-pill px-3 py-2 fw-semibold" style={{ background: "#28B0C415", color: "#28B0C4" }}>
                            {product.productCategory?.name || 'Uncategorized'}
                        </Badge>
                        <h1 className="fw-bold text-dark mb-1">{product.name}</h1>
                        
                        <div className="d-flex align-items-baseline gap-2 mb-4">
                            <div className="fs-2 fw-bold" style={{ color: "#28B0C4" }}>
                                {formatCurrency(currentPrice, currency)}
                            </div>
                            <span className="text-muted">/ unit</span>
                        </div>
                        
                        {product.pricingTiers?.length > 0 && (
                            <div className="bg-light rounded-4 p-4 mb-4">
                                <h6 className="fw-bold text-dark mb-3 small text-uppercase">Volume Pricing</h6>
                                <div className="d-flex flex-column gap-2">
                                    {/* Default Price */}
                                    <div className={`d-flex justify-content-between p-2 rounded-3 ${quantity < (product.pricingTiers[0]?.minQuantity || 0) ? 'bg-white shadow-sm border-start border-4 border-primary' : ''}`}>
                                        <span className="small text-muted">1 - {(product.pricingTiers[0]?.minQuantity - 1) || 'Any'} units</span>
                                        <span className="fw-bold">{formatCurrency(product.price, currency)}</span>
                                    </div>
                                    {/* Tiers */}
                                    {product.pricingTiers.map((tier, idx) => {
                                        const nextMin = product.pricingTiers[idx + 1]?.minQuantity;
                                        const isActive = quantity >= tier.minQuantity && (!nextMin || quantity < nextMin);
                                        return (
                                            <div key={idx} className={`d-flex justify-content-between p-2 rounded-3 ${isActive ? 'bg-white shadow-sm border-start border-4 border-primary' : ''}`}>
                                                <span className="small text-muted">{tier.minQuantity}{nextMin ? ` - ${nextMin - 1}` : '+'} units</span>
                                                <span className="fw-bold">{formatCurrency(tier.price, currency)}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="mb-4">
                            <h6 className="fw-bold text-dark mb-2">Description</h6>
                            <p className="text-muted" style={{ lineHeight: 1.8 }}>
                                {product.description || 'No description available for this product.'}
                            </p>
                        </div>

                        <hr className="my-4" />

                        <div className="d-flex align-items-center gap-4 mb-4">
                            <div className="quantity-selector d-flex align-items-center bg-light rounded-4 p-2">
                                <Button 
                                    variant="white" 
                                    className="rounded-3 shadow-sm border-0 p-2"
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                >
                                    <LuMinus size={18} />
                                </Button>
                                <Form.Control 
                                    type="number" 
                                    className="text-center fw-bold fs-5 bg-transparent border-0 px-2 shadow-none"
                                    style={{ width: '70px' }}
                                    value={quantity}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value);
                                        if (!isNaN(val)) {
                                            setQuantity(Math.max(1, val));
                                        } else {
                                            setQuantity('');
                                        }
                                    }}
                                    onBlur={() => {
                                        if (!quantity || quantity < 1) {
                                            setQuantity(1);
                                        }
                                    }}
                                    min="1"
                                />
                                <Button 
                                    variant="white" 
                                    className="rounded-3 shadow-sm border-0 p-2"
                                    onClick={() => setQuantity(quantity + 1)}
                                >
                                    <LuPlus size={18} />
                                </Button>
                            </div>
                            
                            <Button 
                                className="flex-grow-1 rounded-4 py-3 fw-bold text-white border-0 shadow-lg"
                                style={{ background: "#28B0C4" }}
                                onClick={handleAddToCart}
                            >
                                {cart?.items?.find(i => i.productId === id) ? 'Update Cart' : 'Add to Cart'}
                            </Button>
                        </div>
                        
                        <div className="d-flex justify-content-between align-items-center">
                            {product.quantity > 0 ? (
                                <div className="text-success small fw-semibold">✓ In stock and ready to ship</div>
                            ) : product.productType === 'item' ? (
                                <div className="text-danger small fw-semibold">✗ Currently out of stock</div>
                            ) : <div className="text-success small fw-semibold">✓ Service Available</div>}
                            
                            <div className="fw-bold fs-5">
                                Total: <span style={{ color: "#28B0C4" }}>{formatCurrency(currentPrice * quantity, currency)}</span>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default PortalProductDetail;
